import { hashEmail } from '@/lib/utils/cryptoUtils'

import { getQuizUserId } from './gtmUtils'
import type {
  AccordionClickEvent,
  AccountCardViewEvent,
  CardType,
  CheckoutLoadStepEvent,
  CheckoutLoadStepEventName,
  CarouselChangeEvent,
  CheckoutUnsupportedEvent,
  CtaEvent,
  EligibilityErrorType,
  EligibilityFailedEvent,
  EmailListType,
  GtmEvent,
  IncentiveTimerExpiredEvent,
  IncentiveTimerPage,
  IncentiveType,
  LandingViewEvent,
  LearnPageViewEvent,
  LinkPosition,
  LoginEvent,
  OptionalCtaParams,
  PageViewEvent,
  ProductType,
  Section,
  SignUpEvent,
  VideoEvent,
  WaitlistSignUpEvent,
} from './typesV2'

const sendGtmEvent = (event: GtmEvent) => {
  if (typeof window === 'undefined') {
    return
  }

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(event)
}

const PLATFORM_NUTRITION_SITE = 'nutrition_site'

export const trackCtaClick = (section: Section, textOrImageUrl: string, params?: OptionalCtaParams) => {
  const { email, ...optionalCtaParams } = params || {}
  const event: CtaEvent = {
    platform: PLATFORM_NUTRITION_SITE,
    event: 'user_click',
    click_type: 'cta',
    section: section,
    link_text_image: textOrImageUrl,
    user_id: getQuizUserId() || hashEmail(email),
    ...optionalCtaParams,
    link_position: params?.link_position ?? 'page_body',
    ...(email && { hashed_email: hashEmail(email) }),
  }
  sendGtmEvent(event)
}

/**
 * Dismissing a modal or drawer. A distinct click_type keeps closes out of the
 * CTA stream, so an open and its close aren't counted as two CTA clicks.
 */
export const trackCloseButtonClick = (section: Section, name: string) => {
  const event: CtaEvent = {
    platform: PLATFORM_NUTRITION_SITE,
    event: 'user_click',
    click_type: 'close_button',
    section,
    link_text_image: name,
    user_id: getQuizUserId(),
    link_position: 'modal_close',
  }
  sendGtmEvent(event)
}

// Inline hyperlink inside editor-authored structured text (rich-text bodies,
// accordion content). A distinct click_type 'link' keeps these incidental link
// clicks out of the deliberate-CTA click stream. Keyed on the destination href,
// which is stable across locales unlike the link's visible text.
export const trackInlineLinkClick = (section: Section, href: string) => {
  const event: CtaEvent = {
    platform: PLATFORM_NUTRITION_SITE,
    event: 'user_click',
    click_type: 'link',
    section,
    link_text_image: href,
    user_id: getQuizUserId(),
    link_position: 'page_body',
  }
  sendGtmEvent(event)
}

export const trackPageView = (section: Section, pageName: string) => {
  const event: PageViewEvent = {
    platform: PLATFORM_NUTRITION_SITE,
    event: 'page_view',
    section,
    user_id: getQuizUserId(),
    page_name: pageName,
  }
  sendGtmEvent(event)
}

export const trackVideoEvent = (
  section: Section,
  action: 'Play' | 'Pause' | 'Complete',
  videoTitle: string,
  videoWatched?: number,
) => {
  const gtmEvent: VideoEvent = {
    event: 'video_action',
    section: section,
    platform: PLATFORM_NUTRITION_SITE,
    video_action: action,
    video_name: videoTitle,
    video_length: videoWatched,
    user_id: getQuizUserId(),
  }
  sendGtmEvent(gtmEvent)
}

export const trackLandingViewEvent = (section: Section, pageName: string, pageVersion: string) => {
  const event: LandingViewEvent = {
    platform: PLATFORM_NUTRITION_SITE,
    event: 'landing_view',
    section: section,
    user_id: getQuizUserId(),
    page_name: pageName,
    page_version: pageVersion,
  }
  sendGtmEvent(event)
}

type TrackSignUpEventProps = {
  section: Section
  email?: string | null
  emailList: EmailListType
  linkPosition?: LinkPosition
  textOrImageUrl?: string
}

export const trackSignupEvent = ({
  section,
  emailList,
  email,
  linkPosition = 'page_body',
  textOrImageUrl,
}: TrackSignUpEventProps) => {
  const event: SignUpEvent = {
    platform: PLATFORM_NUTRITION_SITE,
    event: 'sign_up',
    section: section,
    user_id: getQuizUserId(),
    hashed_email: hashEmail(email),
    email_list: emailList,
    link_position: linkPosition,
    link_text_image: textOrImageUrl,
  }
  sendGtmEvent(event)
}

type TrackWaitListSignUpEventProps = TrackSignUpEventProps & {
  waitlistSelectedCountry: string
  waitlistMarketingConsent: boolean
  waitlistSelectedHearAbout?: string
  waitlistSelectedHearOther?: string
  waitlistSelectedMotivation?: string
}

export const trackWaitlistSignUp = ({
  section,
  email,
  emailList,
  linkPosition = 'page_body',
  waitlistSelectedCountry,
  waitlistSelectedHearAbout,
  waitlistMarketingConsent,
  waitlistSelectedHearOther,
  waitlistSelectedMotivation,
}: TrackWaitListSignUpEventProps) => {
  const event: WaitlistSignUpEvent = {
    platform: PLATFORM_NUTRITION_SITE,
    event: 'sign_up',
    section: section,
    user_id: getQuizUserId(),
    hashed_email: hashEmail(email),
    email_list: emailList,
    link_position: linkPosition,
    waitlist_selected_country: waitlistSelectedCountry,
    waitlist_selected_hear_about: waitlistSelectedHearAbout,
    waitlist_marketing_consent: waitlistMarketingConsent,
    waitlist_selected_hear_other: waitlistSelectedHearOther,
    waitlist_selected_motivation: waitlistSelectedMotivation,
  }
  sendGtmEvent(event)
}

type trackAccordionClickEventProps = {
  section: Section
  textOrImageUrl: string
  linkPosition?: string
}

type trackCardClickEventProps = {
  section: Section
  textOrImageUrl: string
  linkPosition?: string
}

export const trackAccordionClickEvent = ({
  section,
  textOrImageUrl,
  linkPosition = 'page_body',
}: trackAccordionClickEventProps) => {
  const event: AccordionClickEvent = {
    platform: PLATFORM_NUTRITION_SITE,
    event: 'user_click',
    click_type: 'accordion',
    section: section,
    link_text_image: textOrImageUrl,
    user_id: getQuizUserId(),
    link_position: linkPosition,
  }
  sendGtmEvent(event)
}

export const trackCarouselEvent = (textOrImageUrl: string, section: Section | null, linkPosition = 'page_body') => {
  const event: CarouselChangeEvent = {
    platform: PLATFORM_NUTRITION_SITE,
    event: 'user_click',
    click_type: 'carousel',
    section: section || 'cms',
    link_text_image: textOrImageUrl,
    user_id: getQuizUserId(),
    link_position: linkPosition,
  }
  sendGtmEvent(event)
}

// Distinct from trackCarouselEvent on purpose: that one measures how far into a
// row a user gets, and folding filter presses into it would corrupt that metric.
export const trackFilterClick = (textOrImageUrl: string, section: Section | null, linkPosition = 'page_body') => {
  const event: CarouselChangeEvent = {
    platform: PLATFORM_NUTRITION_SITE,
    event: 'user_click',
    click_type: 'filter',
    section: section || 'cms',
    link_text_image: textOrImageUrl,
    user_id: getQuizUserId(),
    link_position: linkPosition,
  }
  sendGtmEvent(event)
}

export const trackCardClickEvent = ({
  section,
  textOrImageUrl,
  linkPosition = 'page_body',
}: trackCardClickEventProps) => {
  const event: AccordionClickEvent = {
    platform: PLATFORM_NUTRITION_SITE,
    event: 'user_click',
    click_type: 'card',
    section: section,
    link_text_image: textOrImageUrl,
    user_id: getQuizUserId(),
    link_position: linkPosition,
  }
  sendGtmEvent(event)
}

export const trackImageGalleryThumbnailClick = ({ section, textOrImageUrl }: trackAccordionClickEventProps) => {
  const event: AccordionClickEvent = {
    platform: PLATFORM_NUTRITION_SITE,
    event: 'user_click',
    click_type: 'image_gallery_thumbnail',
    section: section,
    link_text_image: textOrImageUrl,
    link_position: 'page_body',
  }
  sendGtmEvent(event)
}

type TrackLearnArticlePageViewProps = {
  articleCategory: string
  articleTitle: string
  articleVersion: string
  articleAuthor: string
}

export const trackLearnArticlePageView = ({
  articleCategory,
  articleTitle,
  articleVersion,
  articleAuthor,
}: TrackLearnArticlePageViewProps) => {
  const event: LearnPageViewEvent = {
    event: 'learn_content_viewed',
    platform: PLATFORM_NUTRITION_SITE,
    section: 'learn',
    user_id: getQuizUserId(),
    learn_category: articleCategory,
    page_name: articleTitle,
    page_version: articleVersion,
    page_author: articleAuthor,
  }
  sendGtmEvent(event)
}

type LoginEventProps = {
  section: Section
  productType?: string
}
export const trackLoginEvent = (props: LoginEventProps) => {
  const event: LoginEvent = {
    event: 'login',
    platform: PLATFORM_NUTRITION_SITE,
    section: props.section,
    user_type: 'member',
    user_id: getQuizUserId(),
    page_name: 'login',
    product_type: props.productType,
  }
  sendGtmEvent(event)
}

type TrackAccountCardViewEventProps = {
  productType: ProductType
  cardType: CardType
  email?: string | null
}

const CHECKOUT_PAGE_NAME = 'checkout'

// Checkout identifies people by hashed email, falling back to the quiz cookie for
// shoppers who never entered one — so an event is never dropped for want of an id.
const resolveCheckoutUserId = (email?: string | null) => hashEmail(email) ?? getQuizUserId()

type TrackEligibilityFailedProps = {
  errorType: EligibilityErrorType
  email?: string | null
  method?: string
}

/** Eligibility refused the entered email — today only the "you already have an account" case. */
export const trackEligibilityFailed = ({ errorType, email, method = 'Email' }: TrackEligibilityFailedProps) => {
  const event: EligibilityFailedEvent = {
    platform: PLATFORM_NUTRITION_SITE,
    event: 'eligibility_failed',
    section: 'checkout',
    page_name: CHECKOUT_PAGE_NAME,
    error_type: errorType,
    method,
    user_id: resolveCheckoutUserId(email),
  }
  sendGtmEvent(event)
}

type TrackCheckoutUnsupportedProps = {
  errorType: string
  email?: string | null
}

/** The checkout-not-supported screen was shown, keyed on the payment-service error enum. */
export const trackCheckoutUnsupported = ({ errorType, email }: TrackCheckoutUnsupportedProps) => {
  const event: CheckoutUnsupportedEvent = {
    platform: PLATFORM_NUTRITION_SITE,
    event: 'checkout_unsupported',
    section: 'checkout',
    page_name: CHECKOUT_PAGE_NAME,
    error_type: errorType,
    user_id: resolveCheckoutUserId(email),
  }
  sendGtmEvent(event)
}

type TrackCheckoutLoadStepProps = {
  eventName: CheckoutLoadStepEventName
  attemptId: string
  occurredAtEpochMs: number
  email?: string | null
}

/** A step of the checkout load happened; timestamped so the gap between steps can be measured. */
export const trackCheckoutLoadStep = (props: TrackCheckoutLoadStepProps) => {
  const event: CheckoutLoadStepEvent = {
    platform: PLATFORM_NUTRITION_SITE,
    event: props.eventName,
    section: 'checkout',
    page_name: CHECKOUT_PAGE_NAME,
    user_id: resolveCheckoutUserId(props.email),
    checkout_attempt_id: props.attemptId,
    epoch_ms_occurred_at: props.occurredAtEpochMs,
  }
  sendGtmEvent(event)
}

export const trackAccountCardViewEvent = ({ productType, cardType, email }: TrackAccountCardViewEventProps) => {
  const event: AccountCardViewEvent = {
    platform: PLATFORM_NUTRITION_SITE,
    event: 'view_item',
    section: 'account',
    ...(email && { hashed_email: hashEmail(email) }),
    product_type: productType,
    card_type: cardType,
  }
  sendGtmEvent(event)
}

type TrackIncentiveTimerExpiredProps = {
  pageName: IncentiveTimerPage
  incentiveType: IncentiveType
  userEmail?: string | null
}

export const trackIncentiveTimerExpired = ({ pageName, incentiveType, userEmail }: TrackIncentiveTimerExpiredProps) => {
  const event: IncentiveTimerExpiredEvent = {
    platform: PLATFORM_NUTRITION_SITE,
    event: 'incentive_timer_expired',
    section: pageName,
    page_name: pageName,
    incentive_type: incentiveType,
    user_id: getQuizUserId() || hashEmail(userEmail),
  }
  sendGtmEvent(event)
}
